// ═══════════════════════════════════════════════════════════════════════════════
// PAGE TARIFS ESPACE PRO — AbracadaBati
// Charte graphique identique à Boutique, Cockpit Bâtiment & Équipe Agents IA
// Formules directement visibles dès l'arrivée
// ═══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Check, X, ChevronDown, ChevronUp, Sparkles, ArrowRight, 
  Users, Building2, Crown, Zap, Bot
} from 'lucide-react';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';

// ═══════════════════════════════════════════════════════════════════════════════
// DONNÉES DES OFFRES - Loyers boutiques virtuelles
// Offre Fondateur : -50% à vie pour les 200 premiers (paiement annuel)
// ═══════════════════════════════════════════════════════════════════════════════
const PLANS = [
  {
    id: 'pro',
    name: 'Pro',
    accroche: 'Pour transformer ta présence digitale.',
    monthlyPrice: 50,
    annualPrice: 25, // Offre Fondateur -50%
    monthlyPriceNoEngagement: 50,
    pasDePorte: 250, // Offre Fondateur (au lieu de 500€)
    pasDePorteNormal: 500,
    color: '#FF7A00',
    icon: '⚡',
    popular: false,
    users: '5 utilisateurs',
    storage: '5 Go',
    products: '1 000',
    collabs: '10 /mois',
    cta: 'Choisir Pro',
    ctaLink: '/pro/fondateur',
    microcopy: 'L\'offre idéale pour structurer et vendre.',
    bonusAnnuel: ['Domaine offert', 'Hébergement inclus'],
    features: [
      { text: 'Site internet professionnel', included: true, highlight: true },
      { text: 'Nom de domaine personnalisé', included: true },
      { text: 'Boutique indépendante + Marketplace', included: true },
      { text: 'Assistant IA 24h/24', included: true, highlight: true },
      { text: 'Vendeur IA boutique', included: true },
      { text: 'Abracadachat complet (appels, visios)', included: true },
      { text: 'CRM du bâtiment', included: true },
      { text: 'Devis & Factures', included: true },
      { text: 'Comptes rendus IA', included: true },
    ],
  },
  {
    id: 'business',
    name: 'Business',
    accroche: 'Pour automatiser et gérer une équipe.',
    monthlyPrice: 100,
    annualPrice: 50, // Offre Fondateur -50%
    monthlyPriceNoEngagement: 100,
    pasDePorte: 500, // Offre Fondateur (au lieu de 1000€)
    pasDePorteNormal: 1000,
    color: '#FF7A00',
    icon: '📈',
    popular: true,
    badge: 'LE PLUS CHOISI',
    users: '20 utilisateurs',
    storage: '5 Go',
    products: '10 000',
    collabs: '50 /mois',
    cta: 'Choisir Business',
    ctaLink: '/pro/fondateur',
    microcopy: 'Pour les entreprises qui veulent accélérer.',
    bonusAnnuel: ['Domaine offert', 'Hébergement inclus'],
    features: [
      { text: 'Tout de Pro +', included: true, header: true },
      { text: '4 Agents IA spécialisés :', included: true, highlight: true },
      { text: 'Service client', included: true, bullet: true },
      { text: 'Commercial', included: true, bullet: true },
      { text: 'Administratif', included: true, bullet: true },
      { text: 'Community manager', included: true, bullet: true },
      { text: 'Connexion paiement & CRM', included: true },
      { text: 'Support prioritaire', included: true },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    accroche: 'Infrastructure complète pour passer à l\'échelle.',
    monthlyPrice: 150,
    annualPrice: 75, // Offre Fondateur -50%
    monthlyPriceNoEngagement: 150,
    pasDePorte: 750, // Offre Fondateur (au lieu de 1500€)
    pasDePorteNormal: 1500,
    color: '#F97316',
    icon: '👑',
    popular: false,
    badge: 'COMPLET',
    users: '50 utilisateurs',
    storage: '1 To',
    products: '100 000',
    collabs: 'Illimité',
    cta: 'Choisir Premium',
    ctaLink: '/pro/fondateur',
    microcopy: 'Pour dominer ton marché local.',
    bonusAnnuel: ['Domaine offert', 'Hébergement inclus'],
    features: [
      { text: 'Tout de Business +', included: true, header: true },
      { text: '12 Agents IA spécialisés :', included: true, highlight: true },
      { text: 'Expert SEO', included: true, bullet: true },
      { text: 'Assistant stratégie', included: true, bullet: true },
      { text: 'Toute la Dream Team IA', included: true, bullet: true },
      { text: 'Stockage 1 To', included: true },
      { text: 'Collabs illimitées', included: true },
      { text: 'Support VIP 24/7', included: true },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// TABLEAU COMPARATIF
// ═══════════════════════════════════════════════════════════════════════════════
const COMPARISON_DATA = [
  { category: 'Tarification', features: [
    { name: 'Loyer mensuel (Fondateur)', pro: '25 €/mois', business: '50 €/mois', premium: '75 €/mois' },
    { name: 'Loyer mensuel (standard)', pro: '50 €/mois', business: '100 €/mois', premium: '150 €/mois' },
    { name: 'Pas de porte (Fondateur)', pro: '250 €', business: '500 €', premium: '750 €' },
    { name: 'Pas de porte (standard)', pro: '500 €', business: '1 000 €', premium: '1 500 €' },
  ]},
  { category: 'Capacité', features: [
    { name: 'Utilisateurs', pro: '5', business: '20', premium: '50' },
    { name: 'Stockage', pro: '5 Go', business: '5 Go', premium: '1 To' },
    { name: 'Produits boutique', pro: '1 000', business: '10 000', premium: '100 000' },
    { name: 'Collabs / mois', pro: '10', business: '50', premium: 'Illimité' },
  ]},
  { category: 'Site & Présence', features: [
    { name: 'Nom de domaine', pro: 'Offert', business: 'Offert', premium: 'Offert' },
    { name: 'Site internet dédié', pro: true, business: true, premium: true },
    { name: 'Boutique indépendante', pro: true, business: true, premium: true },
    { name: 'Zéro commission', pro: true, business: true, premium: true },
  ]},
  { category: 'Intelligence Artificielle', features: [
    { name: 'Assistant IA', pro: '24h/24', business: '24h/24', premium: 'VIP' },
    { name: 'Agents IA', pro: '1', business: '4', premium: '12' },
    { name: 'Vendeur IA boutique', pro: true, business: true, premium: true },
  ]},
  { category: 'Communication', features: [
    { name: 'Abracadachat', pro: 'Complet', business: 'Avancé', premium: 'Complet' },
    { name: 'Appels & Visios', pro: true, business: true, premium: true },
    { name: 'Comptes rendus IA', pro: true, business: true, premium: true },
  ]},
  { category: 'Support', features: [
    { name: 'Support', pro: 'Email', business: 'Prioritaire', premium: 'VIP 24/7' },
  ]},
];

// ═══════════════════════════════════════════════════════════════════════════════
// FAQ
// ═══════════════════════════════════════════════════════════════════════════════
const FAQ_DATA = [
  { question: 'Est-ce qu\'AbracadaBati prend une commission ?', answer: 'Non. AbracadaBati fonctionne sans commission sur tes ventes. Tu gardes la valeur de ton travail.' },
  { question: 'Qu\'est-ce que l\'offre Fondateur ?', answer: 'L\'offre Fondateur est réservée aux 200 premiers inscrits. Tu bénéficies de -50% à vie sur le loyer mensuel et sur le pas de porte (frais de mise en place). Paiement annuel uniquement.' },
  { question: 'C\'est quoi le pas de porte ?', answer: 'Le pas de porte couvre le paramétrage complet de ta boutique virtuelle : mise en place, configuration, personnalisation et formation.' },
  { question: 'Le nom de domaine est-il inclus ?', answer: 'Oui, le nom de domaine et l\'hébergement sont inclus dans toutes les formules.' },
  { question: 'Est-ce que ma boutique est indépendante ?', answer: 'Oui. Ta boutique est accessible depuis ton propre site internet avec ton nom de domaine.' },
  { question: 'Puis-je changer de formule plus tard ?', answer: 'Oui. Tu peux évoluer vers une formule supérieure à tout moment.' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANTS
// ═══════════════════════════════════════════════════════════════════════════════

const PlanCard = ({ plan, isAnnual, index }) => {
  const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
  const isPopular = plan.popular;
  const isFondateur = isAnnual; // Offre Fondateur = paiement annuel
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{
        position: 'relative',
        background: isPopular 
          ? 'linear-gradient(145deg, rgba(255,122,0,0.15) 0%, rgba(7,43,87,0.95) 100%)'
          : 'rgba(7, 43, 87, 0.85)',
        backdropFilter: 'blur(20px)',
        borderRadius: 24,
        border: isPopular ? '2px solid rgba(255,122,0,0.6)' : '1px solid rgba(255,255,255,0.1)',
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 650,
        transform: isPopular ? 'scale(1.03)' : 'scale(1)',
        boxShadow: isPopular 
          ? '0 25px 60px rgba(255,122,0,0.2), 0 0 40px rgba(255,122,0,0.1)'
          : '0 20px 50px rgba(0,0,0,0.3)',
        zIndex: isPopular ? 10 : 1,
      }}
    >
      {plan.badge && (
        <div style={{
          position: 'absolute',
          top: -14,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #FF7A00 0%, #FF8C1A 100%)',
          color: '#fff',
          padding: '8px 18px',
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 1,
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          whiteSpace: 'nowrap',
        }}>
          {plan.badge}
        </div>
      )}
      
      {/* SECTION 1: Icône + Nom + Accroche - HAUTEUR FIXE */}
      <div style={{ textAlign: 'center', height: 110, marginBottom: 16 }}>
        <div style={{ fontSize: 36, marginBottom: 8, height: 40 }}>{plan.icon}</div>
        <h3 style={{ fontSize: 26, fontWeight: 800, color: plan.color, marginBottom: 6, height: 32 }}>{plan.name}</h3>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.4, height: 36 }}>{plan.accroche}</p>
      </div>
      
      {/* SECTION 2: Loyer mensuel */}
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
          Loyer mensuel
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4 }}>
          {isFondateur && (
            <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through', marginRight: 8 }}>
              {plan.monthlyPrice}€
            </span>
          )}
          <span style={{ fontSize: 48, fontWeight: 800, color: '#fff' }}>{price}</span>
          <span style={{ fontSize: 20, color: '#fff', fontWeight: 600 }}>€</span>
          <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>/mois</span>
        </div>
        <p style={{ fontSize: 12, color: isFondateur ? '#22C55E' : 'rgba(255,255,255,0.5)' }}>
          {isFondateur ? '🎁 Offre Fondateur -50% à vie' : 'paiement mensuel'}
        </p>
      </div>

      {/* SECTION 3: Pas de porte */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: 16,
        padding: '12px',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
      }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
          Pas de porte (mise en place)
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4 }}>
          {isFondateur && (
            <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through', marginRight: 8 }}>
              {plan.pasDePorteNormal}€
            </span>
          )}
          <span style={{ fontSize: 28, fontWeight: 700, color: plan.color }}>
            {isFondateur ? plan.pasDePorte : plan.pasDePorteNormal}
          </span>
          <span style={{ fontSize: 16, color: plan.color, fontWeight: 600 }}>€</span>
        </div>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
          Paramétrage complet de ta boutique
        </p>
      </div>

      {/* SECTION 4: Badges bonus annuel */}
      <div style={{ height: 40, marginBottom: 12 }}>
        {isFondateur && plan.bonusAnnuel ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
            {plan.bonusAnnuel.map((bonus, i) => (
              <span key={i} style={{
                background: `${plan.color}20`,
                color: plan.color,
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 700,
              }}>
                {bonus}
              </span>
            ))}
          </div>
        ) : (
          <div style={{ height: 24 }} />
        )}
      </div>
      
      {/* SECTION 5: Métriques - HAUTEUR FIXE */}
      <div style={{ 
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
        padding: '14px 0', borderTop: '1px solid rgba(255,255,255,0.1)', borderBottom: '1px solid rgba(255,255,255,0.1)',
        marginBottom: 16, height: 60,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Utilisateurs</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{plan.users}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Produits</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{plan.products}</div>
        </div>
      </div>
      
      {/* SECTION 6: Features - HAUTEUR FIXE */}
      <div style={{ height: 220, marginBottom: 16, overflow: 'hidden' }}>
        {plan.features.map((feat, i) => (
          <div key={i} style={{ 
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
            paddingLeft: feat.sub || feat.bullet ? 16 : 0,
            minHeight: 22,
          }}>
            {feat.bullet ? (
              // Tiret pour les sous-éléments
              <span style={{ 
                width: 18, 
                color: 'rgba(255,255,255,0.5)', 
                fontSize: 14,
                textAlign: 'center',
                flexShrink: 0,
              }}>–</span>
            ) : feat.included ? (
              <div style={{
                width: 18, height: 18, borderRadius: '50%',
                background: feat.highlight ? plan.color : '#22C55E',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Check size={11} color="#fff" strokeWidth={3} />
              </div>
            ) : (
              <X size={18} color="rgba(255,255,255,0.3)" style={{ flexShrink: 0 }} />
            )}
            <span style={{ 
              fontSize: feat.sub || feat.bullet ? 12 : 13, 
              color: feat.header ? plan.color : (feat.included ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.4)'),
              fontWeight: feat.header || feat.highlight ? 600 : 400,
              lineHeight: 1.3,
            }}>
              {feat.text}
            </span>
          </div>
        ))}
      </div>
      
      {/* SECTION 6: CTA + Microcopy - HAUTEUR FIXE */}
      <div style={{ height: 80 }}>
        <Link
          to={plan.ctaLink}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '14px 24px', borderRadius: 14, height: 50,
            background: plan.id === 'gratuit' 
              ? 'transparent'
              : `linear-gradient(135deg, ${plan.color} 0%, ${plan.color}CC 100%)`,
            border: plan.id === 'gratuit' ? '2px solid rgba(255,255,255,0.2)' : 'none',
            color: '#fff', fontSize: 15, fontWeight: 700, textDecoration: 'none',
            boxShadow: plan.id !== 'gratuit' ? `0 8px 24px ${plan.color}40` : 'none',
          }}
        >
          {plan.cta}
          <ArrowRight size={18} />
        </Link>
      
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginTop: 10, height: 16 }}>
          {plan.microcopy || '\u00A0'}
        </p>
      </div>
    </motion.div>
  );
};

const FAQItem = ({ item, isOpen, onClick }) => (
  <div style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 0', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 15, fontWeight: 600, color: '#fff', paddingRight: 16 }}>{item.question}</span>
      {isOpen ? <ChevronUp size={20} color="rgba(255,255,255,0.5)" /> : <ChevronDown size={20} color="rgba(255,255,255,0.5)" />}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, paddingBottom: 20 }}>
            {item.answer}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
const EspaceProTarifsPage = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <ImmersiveEspaceProWrapper>
      <UnifiedHeader />
      <EspaceProNav activeSection="tarifs" />

      {/* ═══════════════════════════════════════════════════════════════════════
          HERO + GRILLE TARIFAIRE
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{
        position: 'relative',
        overflow: 'hidden',
        padding: '60px 24px 80px',
        background: `
          radial-gradient(ellipse 80% 60% at 65% 40%, rgba(29,78,137,0.5) 0%, transparent 55%),
          radial-gradient(circle at 20% 80%, rgba(255,122,0,0.08) 0%, transparent 40%),
          linear-gradient(165deg, #072B57 0%, #103A6D 35%, #1D4E89 70%, #072B57 100%)
        `,
      }}>
        {/* Texture subtile */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E\")",
          opacity: 0.03, pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* En-tête compact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ textAlign: 'center', marginBottom: 40 }}
          >
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,122,0,0.15)', border: '1px solid rgba(255,122,0,0.4)',
              padding: '10px 20px', borderRadius: 30, fontSize: 14, fontWeight: 600, color: '#FF7A00',
              marginBottom: 20,
            }}>
              <Sparkles size={16} />
              Tarifs transparents, sans surprise
            </span>

            <h1 style={{
              fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 800, color: '#fff',
              lineHeight: 1.2, marginBottom: 12,
            }}>
              Choisis la formule <span style={{ color: '#FF7A00' }}>qui te correspond</span>
            </h1>

            <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.7)', maxWidth: 600, margin: '0 auto 24px' }}>
              Loue ta boutique virtuelle. Zéro commission sur tes ventes.
            </p>

            {/* Trait orange */}
            <div style={{ width: 60, height: 4, background: '#FF7A00', borderRadius: 2, margin: '0 auto 32px' }} />

            {/* Toggle Mensuel / Fondateur */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: !isAnnual ? '#fff' : 'rgba(255,255,255,0.5)' }}>Tarif standard</span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                style={{
                  position: 'relative', width: 56, height: 30, borderRadius: 15,
                  background: isAnnual ? 'linear-gradient(135deg, #FF7A00 0%, #FF8C1A 100%)' : 'rgba(255,255,255,0.2)',
                  border: 'none', cursor: 'pointer',
                }}
              >
                <div style={{
                  position: 'absolute', top: 3, left: isAnnual ? 29 : 3,
                  width: 24, height: 24, borderRadius: '50%', background: '#fff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)', transition: 'left 0.3s ease',
                }} />
              </button>
              <span style={{ fontSize: 15, fontWeight: 600, color: isAnnual ? '#fff' : 'rgba(255,255,255,0.5)' }}>Offre Fondateur</span>
              {isAnnual && (
                <motion.span 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  style={{
                    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                    color: '#fff', padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                  }}
                >
                  -50% à vie (200 places)
                </motion.span>
              )}
            </div>
          </motion.div>

          {/* Grille tarifaire */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 20,
            alignItems: 'start',
          }}>
            {PLANS.map((plan, index) => (
              <PlanCard key={plan.id} plan={plan} isAnnual={isAnnual} index={index} />
            ))}
          </div>

          {/* Rassurance */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{ textAlign: 'center', marginTop: 40, fontSize: 14, color: 'rgba(255,255,255,0.5)' }}
          >
            0 € pour démarrer · Sans engagement · Évolutif à tout moment · Tous les prix sont HT
          </motion.p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          TABLEAU COMPARATIF
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{
        padding: '80px 24px',
        background: 'linear-gradient(180deg, #072B57 0%, #103A6D 100%)',
      }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ 
              fontSize: 'clamp(28px, 4vw, 36px)', fontWeight: 800, color: '#fff',
              textAlign: 'center', marginBottom: 16,
            }}
          >
            Comparatif <span style={{ color: '#FF7A00' }}>détaillé</span>
          </motion.h2>
          
          <div style={{ width: 60, height: 4, background: '#FF7A00', borderRadius: 2, margin: '0 auto 40px' }} />

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 550 }}>
              <thead>
                <tr style={{ background: 'rgba(255,122,0,0.1)' }}>
                  <th style={{ padding: '16px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    Fonctionnalités
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#FF7A00', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Pro</th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#FF7A00', borderBottom: '1px solid rgba(255,122,0,0.4)', background: 'rgba(255,122,0,0.08)' }}>Business</th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#F97316', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Premium</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_DATA.map((cat, catIndex) => (
                  <>
                    <tr key={`cat-${catIndex}`}>
                      <td colSpan={4} style={{ 
                        padding: '12px 16px', background: 'rgba(255,255,255,0.03)',
                        fontSize: 12, fontWeight: 700, color: '#FF7A00', textTransform: 'uppercase', letterSpacing: 0.5,
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                      }}>
                        {cat.category}
                      </td>
                    </tr>
                    {cat.features.map((feat, featIndex) => (
                      <tr key={`feat-${catIndex}-${featIndex}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '10px 16px', fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{feat.name}</td>
                        {['pro', 'business', 'premium'].map((planId) => {
                          const val = feat[planId];
                          const isBusiness = planId === 'business';
                          return (
                            <td key={planId} style={{ 
                              padding: '10px 12px', textAlign: 'center',
                              background: isBusiness ? 'rgba(255,122,0,0.05)' : 'transparent',
                            }}>
                              {val === true ? (
                                <Check size={16} color="#22C55E" />
                              ) : val === false ? (
                                <X size={16} color="rgba(255,255,255,0.2)" />
                              ) : (
                                <span style={{ 
                                  fontSize: 12, 
                                  color: val === 'Illimité' || val === 'VIP' || val === 'VIP 24/7' ? '#FF7A00' : 'rgba(255,255,255,0.7)',
                                  fontWeight: val === 'Illimité' || val === 'VIP' ? 600 : 400,
                                }}>
                                  {val}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          FAQ
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{
        padding: '80px 24px',
        background: 'linear-gradient(180deg, #103A6D 0%, #1D4E89 100%)',
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ 
              fontSize: 'clamp(28px, 4vw, 36px)', fontWeight: 800, color: '#fff',
              textAlign: 'center', marginBottom: 16,
            }}
          >
            Questions fréquentes
          </motion.h2>
          
          <div style={{ width: 60, height: 4, background: '#FF7A00', borderRadius: 2, margin: '0 auto 40px' }} />

          <div>
            {FAQ_DATA.map((item, i) => (
              <FAQItem key={i} item={item} isOpen={openFAQ === i} onClick={() => setOpenFAQ(openFAQ === i ? null : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          SERVICE CLÉ EN MAIN
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{
        padding: '80px 24px',
        background: 'linear-gradient(180deg, #f4f9ff 0%, #ffffff 100%)',
        fontFamily: "'DM Sans', sans-serif",
        color: '#1a2a4a',
      }}>
        <style>{`
          .tarifs-clem-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
          }
          .tarifs-clem-benefits {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
          }
          .tarifs-clem-main-card {
            display: grid;
            grid-template-columns: 1.1fr 1fr;
            gap: 38px;
          }
          @media (max-width: 1024px) {
            .tarifs-clem-main-card { grid-template-columns: 1fr; }
          }
          @media (max-width: 900px) {
            .tarifs-clem-grid { grid-template-columns: 1fr; }
            .tarifs-clem-benefits { grid-template-columns: 1fr; }
          }
        `}</style>

        <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
          
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              borderRadius: 999,
              background: '#eaf3ff',
              color: '#1264e8',
              fontWeight: 700,
              marginBottom: 20,
              border: '1px solid #bfd8ff',
              fontSize: 13,
            }}
          >
            🚀 SERVICE CLÉ EN MAIN
          </motion.div>

          {/* Titre */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(32px, 5vw, 52px)',
              lineHeight: 1.1,
              margin: 0,
              fontWeight: 800,
              letterSpacing: '-0.02em',
            }}
          >
            Tu arrives. <span style={{ color: '#1264e8' }}>Tout tourne déjà.</span>
          </motion.h2>

          {/* Sous-titre */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            style={{
              fontSize: 'clamp(16px, 2vw, 18px)',
              lineHeight: 1.5,
              margin: '16px auto 40px',
              maxWidth: 650,
              color: 'rgba(26, 42, 74, 0.7)',
              fontWeight: 500,
            }}
          >
            On s'occupe de tout.<br />
            Tu récupères un espace prêt à vendre.
          </motion.p>

          {/* Grande carte centrale avec Dashboard Mockup */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25 }}
            className="tarifs-clem-main-card"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.1fr 1fr',
              gap: 38,
              background: 'white',
              border: '1px solid #cfe2ff',
              borderRadius: 28,
              boxShadow: '0 30px 90px rgba(18, 100, 232, 0.14)',
              padding: 38,
              alignItems: 'center',
              textAlign: 'left',
              marginBottom: 28,
            }}
          >
            {/* Dashboard Mockup */}
            <div style={{
              background: 'linear-gradient(135deg, #eef6ff, #ffffff)',
              borderRadius: 24,
              padding: 28,
              border: '1px solid #d8e9ff',
              minHeight: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div style={{
                width: '100%',
                maxWidth: 520,
                background: 'white',
                borderRadius: 22,
                boxShadow: '0 20px 60px rgba(26, 42, 74, 0.12)',
                padding: 26,
              }}>
                <div style={{ fontWeight: 800, color: '#1a2a4a', marginBottom: 14, fontSize: 18 }}>
                  Mon Espace Pro
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 14,
                  marginBottom: 18,
                }}>
                  <div style={{ background: '#f4f9ff', borderRadius: 14, padding: 16, fontSize: 14 }}>
                    Ventes<br /><strong style={{ fontSize: 20 }}>24 650€</strong>
                  </div>
                  <div style={{ background: '#f4f9ff', borderRadius: 14, padding: 16, fontSize: 14 }}>
                    Commandes<br /><strong style={{ fontSize: 20 }}>128</strong>
                  </div>
                  <div style={{ background: '#f4f9ff', borderRadius: 14, padding: 16, fontSize: 14 }}>
                    Clients<br /><strong style={{ fontSize: 20 }}>356</strong>
                  </div>
                </div>
                <div style={{
                  background: '#e9fff3',
                  color: '#15803d',
                  display: 'inline-block',
                  padding: '6px 12px',
                  borderRadius: 999,
                  fontWeight: 800,
                  fontSize: 13,
                }}>
                  En ligne
                </div>
              </div>
            </div>

            {/* Installation info */}
            <div>
              <h3 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 'clamp(26px, 3vw, 34px)',
                lineHeight: 1.1,
                margin: '0 0 22px',
                fontWeight: 800,
              }}>
                Installation clé en main
              </h3>

              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 28px',
                fontSize: 18,
                lineHeight: 1.9,
                fontWeight: 600,
              }}>
                <li>✅ Site créé et personnalisé à ton image</li>
                <li>✅ Boutique prête avec tes produits / prestations</li>
                <li>✅ Agents IA configurés et opérationnels</li>
                <li>✅ Paiements et livraisons connectés</li>
                <li>✅ Automatisations et emails activés</li>
              </ul>

              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                background: '#eaf3ff',
                color: '#1264e8',
                border: '1px solid #bfd8ff',
                borderRadius: 16,
                padding: '16px 24px',
                fontSize: 24,
                fontWeight: 900,
              }}>
                📅 LIVRÉ EN 7 JOURS
              </div>
            </div>
          </motion.div>

          {/* 3 Cartes Offres */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="tarifs-clem-grid"
            style={{ marginBottom: 26, textAlign: 'left' }}
          >
            {/* PRO */}
            <div style={{
              background: 'white',
              border: '1px solid #cfe2ff',
              borderRadius: 22,
              padding: 28,
              boxShadow: '0 18px 50px rgba(18, 100, 232, 0.10)',
            }}>
              <h3 style={{ color: '#1264e8', fontSize: 30, margin: 0, fontWeight: 900 }}>PRO</h3>
              <p style={{ margin: '4px 0 20px', fontWeight: 700 }}>Installation clé en main</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', lineHeight: 1.8, fontWeight: 600, fontSize: 15 }}>
                <li>✅ Site créé et personnalisé</li>
                <li>✅ Prestations intégrées</li>
                <li>✅ Assistant IA configuré</li>
                <li>✅ Nom de domaine connecté</li>
                <li>✅ Boutique prête à vendre</li>
              </ul>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderLeft: '3px solid #d8e9ff',
                paddingLeft: 20,
                margin: '20px 0',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>Paiement unique</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ textDecoration: 'line-through', color: '#7a8495', fontSize: 24 }}>500€</div>
                  <div style={{ color: '#1264e8', fontSize: 48, fontWeight: 900 }}>250€</div>
                </div>
              </div>
              <div style={{
                background: '#eaf3ff',
                color: '#1264e8',
                borderRadius: 999,
                display: 'inline-block',
                padding: '6px 14px',
                fontWeight: 800,
                marginBottom: 18,
                fontSize: 12,
              }}>
                OFFRE DE PRÉLANCEMENT
              </div>
              <button style={{
                width: '100%',
                border: 'none',
                borderRadius: 14,
                background: '#1264e8',
                color: 'white',
                padding: 16,
                fontSize: 18,
                fontWeight: 900,
                cursor: 'pointer',
              }}>
                Choisir Pro
              </button>
            </div>

            {/* BUSINESS - Mise en avant */}
            <div style={{
              background: 'white',
              border: '2px solid #E8650A',
              borderRadius: 22,
              padding: 28,
              boxShadow: '0 26px 70px rgba(232, 101, 10, 0.18)',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                top: -18,
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#E8650A',
                color: 'white',
                borderRadius: 999,
                padding: '9px 22px',
                fontWeight: 900,
                fontSize: 13,
                whiteSpace: 'nowrap',
              }}>
                ⭐ LE PLUS CHOISI
              </div>
              <h3 style={{ color: '#E8650A', fontSize: 30, margin: 0, fontWeight: 900, marginTop: 8 }}>BUSINESS</h3>
              <p style={{ margin: '4px 0 20px', fontWeight: 700 }}>Système complet</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', lineHeight: 1.8, fontWeight: 600, fontSize: 15 }}>
                <li>✅ Tout ce qui est inclus dans Pro</li>
                <li>✅ 4 agents IA configurés</li>
                <li>✅ Automatisations avancées</li>
                <li>✅ Organisation complète</li>
                <li>✅ Tests et optimisation</li>
              </ul>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderLeft: '3px solid #fce4d4',
                paddingLeft: 20,
                margin: '20px 0',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>Paiement unique</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ textDecoration: 'line-through', color: '#7a8495', fontSize: 24 }}>1000€</div>
                  <div style={{ color: '#E8650A', fontSize: 48, fontWeight: 900 }}>500€</div>
                </div>
              </div>
              <div style={{
                background: '#fce4d4',
                color: '#E8650A',
                borderRadius: 999,
                display: 'inline-block',
                padding: '6px 14px',
                fontWeight: 800,
                marginBottom: 18,
                fontSize: 12,
              }}>
                OFFRE DE PRÉLANCEMENT
              </div>
              <button style={{
                width: '100%',
                border: 'none',
                borderRadius: 14,
                background: '#E8650A',
                color: 'white',
                padding: 16,
                fontSize: 18,
                fontWeight: 900,
                cursor: 'pointer',
              }}>
                Choisir Business
              </button>
            </div>

            {/* PREMIUM */}
            <div style={{
              background: 'white',
              border: '1px solid #dec8ff',
              borderRadius: 22,
              padding: 28,
              boxShadow: '0 18px 50px rgba(124, 58, 237, 0.12)',
            }}>
              <h3 style={{ color: '#7c3aed', fontSize: 30, margin: 0, fontWeight: 900 }}>PREMIUM</h3>
              <p style={{ margin: '4px 0 20px', fontWeight: 700 }}>Machine complète</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', lineHeight: 1.8, fontWeight: 600, fontSize: 15 }}>
                <li>✅ Tout ce qui est inclus dans Business</li>
                <li>✅ 12 agents IA configurés</li>
                <li>✅ Dream Team complète</li>
                <li>✅ Stratégie et structure complètes</li>
                <li>✅ Accompagnement prioritaire</li>
              </ul>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderLeft: '3px solid #eadcff',
                paddingLeft: 20,
                margin: '20px 0',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>Paiement unique</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ textDecoration: 'line-through', color: '#7a8495', fontSize: 24 }}>1500€</div>
                  <div style={{ color: '#7c3aed', fontSize: 48, fontWeight: 900 }}>750€</div>
                </div>
              </div>
              <div style={{
                background: '#eadcff',
                color: '#7c3aed',
                borderRadius: 999,
                display: 'inline-block',
                padding: '6px 14px',
                fontWeight: 800,
                marginBottom: 18,
                fontSize: 12,
              }}>
                OFFRE DE PRÉLANCEMENT
              </div>
              <button style={{
                width: '100%',
                border: 'none',
                borderRadius: 14,
                background: '#7c3aed',
                color: 'white',
                padding: 16,
                fontSize: 18,
                fontWeight: 900,
                cursor: 'pointer',
              }}>
                Choisir Premium
              </button>
            </div>
          </motion.div>

          {/* Bandeau 3 bénéfices */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="tarifs-clem-benefits"
            style={{
              background: 'white',
              border: '1px solid #cfe2ff',
              borderRadius: 22,
              padding: 20,
              boxShadow: '0 18px 50px rgba(18, 100, 232, 0.08)',
              textAlign: 'center',
            }}
          >
            <div style={{ padding: '10px 0' }}>
              <strong style={{ fontSize: 16 }}>🕒 Livré en 7 jours</strong><br />
              <span style={{ color: 'rgba(26, 42, 74, 0.7)', fontSize: 14 }}>Ton espace prêt à l'emploi</span>
            </div>
            <div style={{ padding: '10px 0' }}>
              <strong style={{ fontSize: 16 }}>🛡️ Zéro prise de tête</strong><br />
              <span style={{ color: 'rgba(26, 42, 74, 0.7)', fontSize: 14 }}>On s'occupe de tout</span>
            </div>
            <div style={{ padding: '10px 0' }}>
              <strong style={{ fontSize: 16 }}>🎯 Prêt à vendre</strong><br />
              <span style={{ color: 'rgba(26, 42, 74, 0.7)', fontSize: 14 }}>Tu n'as plus qu'à encaisser</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          CTA FINAL
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{
        padding: '80px 24px',
        background: `
          radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255,122,0,0.12) 0%, transparent 60%),
          linear-gradient(180deg, #1D4E89 0%, #072B57 100%)
        `,
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span style={{ fontSize: 48, marginBottom: 20, display: 'block' }}>🚀</span>
            
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 800, color: '#fff', marginBottom: 16 }}>
              Prêt à transformer <span style={{ color: '#FF7A00' }}>ton entreprise ?</span>
            </h2>
            
            <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.7)', marginBottom: 32, lineHeight: 1.6 }}>
              Rejoins les Membres Fondateurs et bénéficie d'avantages exclusifs.
            </p>
            
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link
                to="/pro/fondateur"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '18px 32px', borderRadius: 14,
                  background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                  color: '#fff', fontWeight: 700, fontSize: 16, textDecoration: 'none',
                  boxShadow: '0 8px 30px rgba(34,197,94,0.35)',
                }}
              >
                <Crown size={20} />
                Devenir Membre Fondateur
              </Link>
              
              <Link
                to="/pro/equipe-agents-ia"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '18px 32px', borderRadius: 14,
                  background: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,255,255,0.2)',
                  color: '#fff', fontWeight: 600, fontSize: 16, textDecoration: 'none',
                }}
              >
                Découvrir les Agents IA
              </Link>
            </div>
            
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 24 }}>
              Sans commission · Sans intermédiaire · Sans engagement sur l'offre mensuelle
            </p>
          </motion.div>
        </div>
      </section>

      
    </ImmersiveEspaceProWrapper>
  );
};

export default EspaceProTarifsPage;
